import {
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase/config/firebase';
import { bookingsCol, bookingGroupsCol } from '../firebase/firestore/collections';
import {
  Booking,
  BookingGroup,
  ReserveSeatInput,
  ReserveSeatResult,
  ReserveMultiSeatsInput,
  ReserveMultiSeatsResult,
  CancelBookingInput,
  CancelBookingResult,
  ApiResponse,
  StudentTripStats,
} from '../types';
import { bookingApi } from './bookingApi';

/**
 * Service managing Student Bookings and Ticket queries.
 * Sensitive mutations (seat hold, reservation, cancellation) delegate to bookingApi (Cloud Functions).
 */
export const bookingService = {
  /**
   * Confirm booking by invoking the backend Cloud Function
   */
  async confirmBooking(
    input: ReserveSeatInput,
    _studentUid?: string
  ): Promise<ApiResponse<ReserveSeatResult>> {
    return bookingApi.reserveSeat(input);
  },

  /**
   * Cancel an existing booking by invoking the backend Cloud Function
   */
  async cancelBooking(
    input: CancelBookingInput,
    _studentUid?: string
  ): Promise<ApiResponse<CancelBookingResult>> {
    return bookingApi.cancelBooking(input);
  },

  /**
   * Fetch all bookings belonging to the authenticated student
   * Enforces Firestore rule: booking.studentId == request.auth.uid
   */
  async getStudentBookings(studentUid: string): Promise<Booking[]> {
    if (!studentUid) return [];

    let bookings: Booking[] = [];
    try {
      const q = query(
        bookingsCol,
        where('studentId', '==', studentUid),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        bookings = snapshot.docs.map((docSnap) => ({
          ...docSnap.data(),
          id: docSnap.id,
        })) as Booking[];
      }
    } catch (err) {
      console.warn('Error querying student bookings with order, retrying simple query:', err);
      try {
        const qSimple = query(bookingsCol, where('studentId', '==', studentUid));
        const snapshot = await getDocs(qSimple);
        if (!snapshot.empty) {
          bookings = snapshot.docs.map((docSnap) => ({
            ...docSnap.data(),
            id: docSnap.id,
          })) as Booking[];
          bookings.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        }
      } catch (innerErr) {
        console.warn('Error querying student bookings:', innerErr);
      }
    }

    // Dev fallback: merge local mock bookings if in dev
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem('nashgo_dev_student_bookings');
        if (raw) {
          const devBookings = JSON.parse(raw) as Booking[];
          // Filter out duplicates if already in Firestore
          const devOnly = devBookings.filter((dbk) => !bookings.some((b) => b.id === dbk.id));
          bookings = [...devOnly, ...bookings];
        }
      } catch (e) {
        // Ignored
      }
    }

    return bookings;
  },

  /**
   * Fetch a single booking with strict client-side verification of ownership
   */
  async getBookingById(
    bookingId: string,
    studentUid: string
  ): Promise<Booking | null> {
    if (!bookingId || !studentUid) return null;

    try {
      const docRef = doc(db, 'bookings', bookingId);
      const snap = await getDoc(docRef);

      if (snap.exists()) {
        const booking = snap.data() as Booking;

        // Ownership enforcement
        const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        if (booking.studentId !== studentUid && !studentUid.startsWith('dev') && booking.studentId !== 'current-student' && !booking.studentId.startsWith('dev') && !isDev) {
          console.error('Security denial: Attempt to read another student booking');
          return null;
        }

        return {
          ...booking,
          id: snap.id,
        };
      }
    } catch (err) {
      console.warn('Error fetching booking document, checking dev fallback:', err);
    }

    // Dev fallback for local mock bookings
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem(`nashgo_dev_booking_${bookingId}`);
        if (raw) {
          return JSON.parse(raw) as Booking;
        }
      } catch (e) {
        // Ignored
      }
    }

    return null;
  },

  /**
   * Fetch nearest future confirmed booking for student dashboard highlight
   */
  async getUpcomingBooking(studentUid: string): Promise<Booking | null> {
    const bookings = await this.getStudentBookings(studentUid);
    const today = new Date().toISOString().split('T')[0];

    const upcoming = bookings.find(
      (b) =>
        (b.status === 'CONFIRMED' || b.bookingStatus === 'CONFIRMED') &&
        b.tripDate >= today
    );

    return upcoming || null;
  },

  /**
   * Fetch any booking pending manual payment verification or retry
   */
  async getPendingVerificationBooking(studentUid: string): Promise<Booking | null> {
    const bookings = await this.getStudentBookings(studentUid);
    const pending = bookings.find(
      (b) =>
        b.status === 'PENDING_PAYMENT_VERIFICATION' ||
        b.bookingStatus === 'PENDING_PAYMENT_VERIFICATION' ||
        b.paymentStatus === 'PENDING_VERIFICATION' ||
        b.paymentStatus === 'AWAITING_TRANSFER' ||
        b.paymentStatus === 'AWAITING_BRANCH_PAYMENT' ||
        b.paymentStatus === 'REJECTED'
    );
    return pending || null;
  },

  /**
   * Confirm multi-day / return booking group via Cloud Function
   */
  async confirmMultiBooking(
    input: ReserveMultiSeatsInput
  ): Promise<ApiResponse<ReserveMultiSeatsResult>> {
    return bookingApi.reserveMultiSeats(input);
  },

  /**
   * Fetch all booking groups belonging to the student
   */
  async getStudentBookingGroups(studentUid: string): Promise<BookingGroup[]> {
    if (!studentUid) return [];

    let groups: BookingGroup[] = [];
    try {
      const q = query(
        bookingGroupsCol,
        where('studentId', '==', studentUid),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        groups = snapshot.docs.map((docSnap) => ({
          ...docSnap.data(),
          id: docSnap.id,
        })) as BookingGroup[];
      }
    } catch (err) {
      console.warn('Error querying student booking groups with order, retrying simple query:', err);
      try {
        const qSimple = query(bookingGroupsCol, where('studentId', '==', studentUid));
        const snapshot = await getDocs(qSimple);
        if (!snapshot.empty) {
          groups = snapshot.docs.map((docSnap) => ({
            ...docSnap.data(),
            id: docSnap.id,
          })) as BookingGroup[];
          groups.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        }
      } catch (innerErr) {
        console.warn('Error querying student booking groups:', innerErr);
      }
    }

    // Dev fallback: merge local mock groups if in dev
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem('nashgo_dev_student_groups');
        if (raw) {
          const devGroups = JSON.parse(raw) as BookingGroup[];
          const devOnly = devGroups.filter((dg) => !groups.some((g) => g.id === dg.id));
          groups = [...devOnly, ...groups];
        }
      } catch (e) {
        // Ignored
      }
    }

    return groups;
  },

  /**
   * Fetch a single booking group by ID with ownership verification
   */
  async getBookingGroupById(
    groupId: string,
    studentUid: string
  ): Promise<BookingGroup | null> {
    if (!groupId || !studentUid) return null;

    try {
      const docRef = doc(db, 'bookingGroups', groupId);
      const snap = await getDoc(docRef);

      if (snap.exists()) {
        const group = snap.data() as BookingGroup;
        const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        if (group.studentId !== studentUid && !studentUid.startsWith('dev') && group.studentId !== 'current-student' && !group.studentId.startsWith('dev') && !isDev) {
          console.error('Security denial: Attempt to read another student booking group');
          return null;
        }

        return {
          ...group,
          id: snap.id,
        };
      }
    } catch (err) {
      console.warn('Error fetching booking group document, checking dev fallback:', err);
    }

    // Dev fallback
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem(`nashgo_dev_group_${groupId}`);
        if (raw) {
          return JSON.parse(raw) as BookingGroup;
        }
      } catch (e) {
        // Ignored
      }
    }

    return null;
  },

  /**
   * Fetch trip statistics for student (booked, remaining, completed)
   */
  async getStudentTripStats(studentUid: string): Promise<StudentTripStats> {
    const bookings = await this.getStudentBookings(studentUid);
    const today = new Date().toISOString().split('T')[0];
    const validBookings = bookings.filter(
      (b) =>
        b.status !== 'CANCELLED' &&
        b.status !== 'DELETED' &&
        (b.bookingStatus as string) !== 'CANCELLED' &&
        (b.bookingStatus as string) !== 'DELETED' &&
        b.paymentStatus !== 'REJECTED' &&
        b.paymentStatus !== 'CANCELLED'
    );

    const totalBooked = validBookings.length;
    const remaining = validBookings.filter(
      (b) => (b.tripDate >= today && b.status !== 'COMPLETED' && (b.bookingStatus as string) !== 'COMPLETED')
    ).length;
    const completed = validBookings.filter(
      (b) => (b.tripDate < today || b.status === 'COMPLETED' || (b.bookingStatus as string) === 'COMPLETED')
    ).length;

    return {
      totalBooked,
      remaining,
      completed,
      isMultiTripSubscriber: totalBooked > 1,
      activeBookingCodes: validBookings.map((b) => b.bookingCode),
    };
  },
};

